import { useForm } from "@inertiajs/react";
import { type ChangeEvent, type ClipboardEvent, type FormEvent, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useFaviconUrl } from "@/hooks/use-favicon-url";
import { useLeadDomain } from "@/hooks/use-lead-domain";
import { cn } from "@/lib/utils";

const DOMAIN_PATTERN = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;

interface HighlightChip {
  icon?: string;
  text: string;
}

interface FinalCtaProps {
  title?: string;
  description?: string;
  highlights?: HighlightChip[];
  submitLabel?: string;
  subtext?: string;
  className?: string;
}

const defaultHighlights: HighlightChip[] = [
  { icon: "⚡️", text: "Unlimited refinements for campaigns" },
  { icon: "📊", text: "Client-ready analytics dashboards" },
];

const stripProtocol = (value: string) => value.replace(/^[a-z]+:\/\//i, "");

const sanitizeUrl = (rawValue: string) => {
  let input = rawValue.trim();
  input = input.replace(/^[\s\u200B-\u200D\uFEFF]+|[\s\u200B-\u200D\uFEFF]+$/g, "");

  if (input && !/^https?:\/\//i.test(input) && /^[\w.-]+\.[a-z]{2,}(\/.*)?$/i.test(input)) {
    input = `https://${input}`;
  }

  input = input.replace(/\s+/g, "");

  return input;
};

export function FinalCta({
  title = "Marketers, turn every share into a client win",
  description = "Connect OG Pilot to your clients’ domains, auto-generate branded OG images, and prove the click-through lift with built-in analytics. No more designer bottlenecks, no more launch delays. Just scroll-stopping previews that close retainers faster.",
  highlights = defaultHighlights,
  submitLabel = "Try OG Pilot",
  subtext = "7-day free trial. No credit card required.",
  className,
}: FinalCtaProps) {
  const { setLeadDomain } = useLeadDomain();
  const inputRef = useRef<HTMLInputElement>(null);
  const domainForm = useForm<{ domain: string }>({ domain: "" });
  const [isValidDomain, setIsValidDomain] = useState(false);
  const faviconUrl = useFaviconUrl(domainForm.data.domain);

  const chips = useMemo(() => (highlights.length > 0 ? highlights : defaultHighlights), [highlights]);

  const updateDomain = (value: string) => {
    const sanitized = stripProtocol(sanitizeUrl(value)).split("/")[0];
    domainForm.setData("domain", sanitized);
    domainForm.clearErrors("domain");

    if (DOMAIN_PATTERN.test(sanitized)) {
      setIsValidDomain(true);
    } else {
      setIsValidDomain(false);
      setLeadDomain(null);
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    updateDomain(event.target.value);
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData("text");
    if (!pastedText) return;
    updateDomain(pastedText);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (domainForm.processing) return;

    const domain = domainForm.data.domain.trim();
    if (!DOMAIN_PATTERN.test(domain)) {
      domainForm.setError("domain", "Enter a valid domain before continuing.");
      inputRef.current?.focus();
      return;
    }

    domainForm.post("/onboarding/add_domain", {
      preserveScroll: true,
      onSuccess: () => {
        setLeadDomain(domain);
        window.location.href = "/sign_up";
      },
      onError: () => {
        inputRef.current?.focus();
      },
    });
  };

  return (
    <section
      className={cn(
        "relative overflow-hidden bg-gradient-to-r from-orange-500 via-orange-600 to-rose-500 py-16 text-white shadow-xl sm:py-24 dark:from-orange-600 dark:via-orange-700 dark:to-rose-600 dark:text-gray-100",
        className,
      )}
    >
      <div className="container mx-auto max-w-6xl px-4">
        <div className="grid gap-12 md:grid-cols-[1.1fr_0.9fr] md:items-center">
          <div className="space-y-6">
            <h2 className="text-3xl font-bold md:text-4xl">{title}</h2>
            <p className="text-sm leading-6 text-orange-50 md:text-base">{description}</p>
            {chips.length > 0 && (
              <div className="flex flex-wrap gap-3 text-xs md:text-sm">
                {chips.map((chip, index) => (
                  <span
                    key={`${chip.text}-${index}`}
                    className="inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2"
                  >
                    {chip.icon && <span className="text-base leading-none">{chip.icon}</span>}
                    {chip.text}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="rounded-3xl bg-white/10 p-8 backdrop-blur">
            <h3 className="text-xl font-semibold">Ready to onboard your next app?</h3>
            <p className="mt-3 text-sm text-orange-50">Start a trial and ship OG images that earn clicks.</p>
            <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
              <div className="flex items-center rounded-md bg-white/90 pr-2 pl-3 text-gray-900 outline-1 -outline-offset-1 outline-gray-200 focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-orange-500 dark:bg-black/60 dark:text-white dark:outline-white/10 dark:focus-within:outline-orange-400">
                <div className="h-7 w-7 shrink-0 rounded text-base text-gray-500 select-none sm:text-sm/6 dark:text-gray-400">
                  {faviconUrl ? (
                    <img src={faviconUrl} alt="Website favicon" className="h-7 w-7 rounded" />
                  ) : (
                    <span className="h-7 w-7 rounded text-xl dark:text-gray-500">🌐</span>
                  )}
                </div>
                <Input
                  ref={inputRef}
                  name="cta-domain"
                  type="text"
                  inputMode="url"
                  placeholder="yourdomain.com"
                  className="flex-1 border-0 bg-transparent text-base shadow-none focus-visible:ring-0"
                  value={domainForm.data.domain}
                  onChange={handleChange}
                  onPaste={handlePaste}
                  disabled={domainForm.processing}
                />
              </div>
              {domainForm.errors.domain ? <p className="text-sm text-red-100">{domainForm.errors.domain}</p> : null}
              <Button
                type="submit"
                className="h-11 w-full bg-white text-orange-600 transition-colors hover:bg-orange-50 focus:ring-2 focus:ring-orange-200 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                disabled={!isValidDomain || domainForm.processing}
              >
                {domainForm.processing ? "Checking..." : submitLabel}
              </Button>
              {subtext && <p className="text-center text-sm text-orange-100">{subtext}</p>}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
